import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const THINKING_LEVELS = [
  { value: 'minimal', ar: 'أسرع رد (بدون تفكير)', en: 'Fastest reply (no thinking)' },
  { value: 'low',     ar: 'سريع (تفكير خفيف)',     en: 'Fast (light thinking)' },
  { value: 'medium',  ar: 'متوازن',                en: 'Balanced' },
  { value: 'high',    ar: 'أعمق تفكير (أبطأ)',     en: 'Deepest thinking (slowest)' },
];

export function AiModelPicker({ providerCode, model, thinkingLevel, onChange, disabled }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (a, e) => (isAr ? a : e);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (refresh = false) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ai-registry/meta/providers`,
        { headers: getAuthHeaders(), params: refresh ? { refresh: 1 } : {} });
      const p = (res.data?.data || []).find((x) => x.code === providerCode);
      setProvider(p || null);
      if (!p) setError(t('المزوّد غير معروف', 'Unknown provider'));
    } catch (err) {
      setError(err?.response?.data?.detail || t('تعذر جلب النماذج', 'Failed to load models'));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [providerCode]);

  const details = provider?.models_meta || [];
  const stable = details.filter((m) => m.stable);
  const preview = details.filter((m) => !m.stable);
  const live = provider?.source === 'google_live';
  const current = details.find((m) => m.id === model);

  return (
    <div className="space-y-3" data-testid="ai-model-picker">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-white/60">{t('النموذج المستخدم', 'Model used')}</label>
        <div className="flex items-center gap-2 text-[10px]">
          {provider && (
            <span className={`px-2 py-0.5 rounded-full border ${live ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-amber-500/15 text-amber-300 border-amber-500/40'}`}
                  data-testid="ai-model-source-badge">
              {live ? t(`مباشر من Google · ${details.length} نموذج`, `Live from Google · ${details.length} models`)
                    : t('قائمة احتياطية', 'Fallback list')}
            </span>
          )}
          <button type="button" onClick={() => load(true)} disabled={loading || disabled}
                  className="text-white/50 hover:text-white disabled:opacity-40" data-testid="ai-model-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <select value={model || ''} disabled={disabled || loading}
              onChange={(e) => onChange({ model_used: e.target.value })}
              className="w-full bg-[#0f0f24] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
              dir="ltr" data-testid="ai-model-select">
        {model && !current && <option value={model}>{model}</option>}
        {stable.length > 0 && (
          <optgroup label={t('مستقر — موصى به للتشغيل', 'Stable — recommended for production')}>
            {stable.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id}{m.id === provider?.recommended_model ? ` ★ ${t('موصى به', 'recommended')}` : ''}
              </option>
            ))}
          </optgroup>
        )}
        {preview.length > 0 && (
          <optgroup label={t('تجريبي (Preview) — قد يكون أبطأ أو يتغير', 'Preview — may be slower or change')}>
            {preview.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
          </optgroup>
        )}
      </select>
      {error && <div className="text-[11px] text-red-300" data-testid="ai-model-error">{error}</div>}
      {current && (
        <div className="text-[11px] text-white/40" dir="ltr">
          {current.display_name} · in {Math.round((current.input_token_limit || 0) / 1000)}k / out {Math.round((current.output_token_limit || 0) / 1000)}k tokens
        </div>
      )}

      <label className="text-xs text-white/60 flex items-center gap-1 pt-1">
        <Zap className="w-3.5 h-3.5 text-[#d4af37]" />{t('سرعة الرد (مستوى التفكير)', 'Reply speed (thinking level)')}
      </label>
      <select value={thinkingLevel || 'minimal'} disabled={disabled}
              onChange={(e) => onChange({ thinking_level: e.target.value })}
              className="w-full bg-[#0f0f24] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
              data-testid="ai-thinking-level-select">
        {THINKING_LEVELS.map((l) => <option key={l.value} value={l.value}>{isAr ? l.ar : l.en}</option>)}
      </select>
      <div className="text-[11px] text-white/40">
        {t('«أسرع رد» هو الأفضل لمحادثات العملاء. النماذج الأقوى (Pro/3.7+/3.8+) تتجاهل «أسرع رد» وتعمل بأقل مستوى تدعمه.',
           '"Fastest reply" is best for customer chat. Heavier models (Pro/3.7+/3.8+) ignore "fastest" and use the lowest level they support.')}
      </div>
    </div>
  );
}
